import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HiOutlineBolt } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import analytics from '../../../services/analytics';
import { useAuth } from '../../../contexts/AuthContext';
import { getPlanLabel } from '../../../utils/planDisplay';
import StatusBanners from '../BillingView/components/StatusBanners';
import CreditTopUps from '../BillingView/components/CreditTopUps';
import CheckoutSuccessModal from '../BillingView/components/CheckoutSuccessModal';
import BalanceCard from './components/BalanceCard';
import HowTopUpsWork from './components/HowTopUpsWork';

const NON_OWNER_TOOLTIP = 'Only the workspace owner can manage subscription options.';

const TopUpsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace, subscription: billingStatus, credits, refreshSubscription } = useAuth();
  const isOwner = currentWorkspace?.role === 'owner';
  const [creditTopUps, setCreditTopUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [notification, setNotification] = useState(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const plansData = await cloudApi.getAvailablePlans();
      setCreditTopUps(plansData.credit_topups || []);
    } catch (error) {
      console.error('Failed to load credit top-ups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      const amount = searchParams.get('amount');
      analytics.trackCheckoutCompleted({ source: 'topups', flow: 'topups', type: 'credits' });
      setCheckoutSuccess({ type: 'credits', creditAmount: amount });
      setTimeout(() => {
        refreshSubscription();
        loadData();
      }, 2000);
    } else if (checkout === 'cancel') {
      analytics.trackCheckoutCancelled({ flow: 'topups' });
      setNotification({ type: 'info', message: 'Checkout was cancelled.' });
    }
    if (checkout) {
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      searchParams.delete('type');
      searchParams.delete('plan');
      searchParams.delete('amount');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadData, refreshSubscription]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const currentPlan = billingStatus?.subscription?.plan?.name || 'free';
  const subscriptionStatus = billingStatus?.subscription?.status;
  const cancelAtPeriodEnd = billingStatus?.subscription?.cancel_at_period_end;
  const nextCreditReplenishment = billingStatus?.subscription?.next_credit_replenishment;
  const planLabel = getPlanLabel(billingStatus);
  const largestPack = creditTopUps.reduce((max, pack) => Math.max(max, pack.credits || 0), 0);

  const handleBuyCredits = async (priceId) => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading(`credits-${priceId}`);
    try {
      const { url } = await cloudApi.createCreditCheckoutSession(priceId);
      analytics.trackCheckoutStarted({ type: 'credits', priceId, source: 'topups', flow: 'topups' });
      window.open(url, '_blank');
    } catch (error) {
      analytics.trackCheckoutFailed({ type: 'credits', priceId, flow: 'topups', reason: error.message });
      setNotification({ type: 'error', message: error.message || 'Failed to start checkout' });
      setActionLoading(null);
    }
  };

  const handleOpenPortal = async () => {
    if (!isOwner) {
      setNotification({ type: 'info', message: NON_OWNER_TOOLTIP });
      return;
    }
    setActionLoading('portal');
    try {
      const { url } = await cloudApi.createPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to open billing portal' });
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-5xl mx-auto pb-16">
      {/* Ambient background orbs — parent card handles overflow clipping */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-blue-500/[0.05] dark:bg-blue-500/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute top-[460px] -right-32 w-[360px] h-[360px] rounded-full bg-purple-500/[0.05] dark:bg-purple-500/[0.07] blur-3xl" />

      {checkoutSuccess && (
        <CheckoutSuccessModal
          type={checkoutSuccess.type}
          plan={null}
          creditAmount={checkoutSuccess.creditAmount}
          onClose={() => setCheckoutSuccess(null)}
        />
      )}

      {notification && (
        <div className={`rounded-lg p-4 border animate-fade-in mb-4 ${
          notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : notification.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{notification.message}</p>
            <button onClick={() => setNotification(null)} className="ml-4 text-sm opacity-60 hover:opacity-100">
              Dismiss
            </button>
          </div>
        </div>
      )}

      <StatusBanners
        subscriptionStatus={subscriptionStatus}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        billingStatus={billingStatus}
        onOpenPortal={handleOpenPortal}
        disabled={!isOwner}
        disabledTooltip={NON_OWNER_TOOLTIP}
      />

      {/* Hero */}
      <div className="relative text-center pt-8 pb-2 animate-fade-in">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-400/20">
          <HiOutlineBolt className="w-3.5 h-3.5" />
          Credit top-ups
        </span>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-gray-100 mb-4 tracking-tight leading-tight">
          Need more{' '}
          <span className="bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
            credits
          </span>?
        </h1>
        <p className="text-base md:text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-relaxed">
          Top up your balance anytime. Packs are valid for 12 months and stack on top of your plan&rsquo;s allowance.
        </p>
      </div>

      {/* Balance */}
      {credits && (
        <div className="mt-8 animate-fade-in" style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}>
          <BalanceCard
            credits={credits}
            planLabel={planLabel}
            nextCreditReplenishment={nextCreditReplenishment}
          />
        </div>
      )}

      {/* Credit top-ups — focal */}
      <div id="credit-topups" className="mt-6 animate-fade-in" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
        <CreditTopUps
          creditTopUps={creditTopUps}
          currentPlan={currentPlan}
          onBuyCredits={handleBuyCredits}
          actionLoading={actionLoading}
          disabled={!isOwner}
          disabledTooltip={NON_OWNER_TOOLTIP}
          currentBalance={credits?.available ?? null}
        />
      </div>

      {/* Good to know */}
      <div className="mt-6 animate-fade-in" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
        <HowTopUpsWork largestPack={largestPack} />
      </div>
    </div>
  );
};

export default TopUpsView;
